import React, { FC, useEffect, useMemo } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  ACTIVITY_EVENT_TYPE,
  ACTIVITY_EVENT_TYPE_FUSION_AUTH,
  DEFAULT_IDP_SOURCE_LABEL,
  IActivity,
  IDP_SOURCE_LABEL,
  USER_ACTIVITY_FIELDS,
} from '../types/activity';
import { GridTooltip } from './General/GridTooltip';
import { ErrorOverlayDataGrid } from './ErrorOverlayDataGrid';
import './UserActivityDataGrid.css';
import { GridRenderCellParams } from '@mui/x-data-grid/models/params/gridCellParams';
import { UsePagedCollectionHookResult } from '../hooks/usePagedCollection';
import { FullIdentity } from '../types/identity';
import { getRowIndex, withRowIndex } from '../util/gridRowIndex';

interface UserActivityDataGridProps {
  pagedCollectionHook: UsePagedCollectionHookResult<IActivity>;
  identity: Pick<FullIdentity, 'id' | 'email'>;
}

const UserActivityDataGrid: FC<UserActivityDataGridProps> = ({ pagedCollectionHook, identity }) => {
  const { collection, isLoading, hasErrors, pageNumber, pageSize, setPageNumber, setPageSize } =
    pagedCollectionHook;

  const rowCount = collection?.total;
  const [rowCountState, setRowCountState] = React.useState(rowCount || 0);

  useEffect(() => {
    setRowCountState((prevRowCountState) =>
      rowCount !== undefined ? rowCount : prevRowCountState
    );
  }, [rowCount, setRowCountState]);

  const rows = useMemo(() => withRowIndex(collection?.data || []), [collection]);

  const columns: GridColDef[] = [
    {
      field: USER_ACTIVITY_FIELDS.TIMESTAMP,
      headerName: 'Timestamp',
      minWidth: 160,
      flex: 1,
      valueGetter: (_value, row) =>
        `${
          row.timestamp
            ? new Date(row.timestamp).toLocaleString(undefined, {
                hour12: false,
              })
            : ''
        }`,
      renderCell: (params: GridRenderCellParams) => <GridTooltip params={params} />,
    },
    {
      field: USER_ACTIVITY_FIELDS.IDP_SOURCE,
      headerName: 'Source',
      minWidth: 110,
      flex: 1,
      valueGetter: (_value, row) => IDP_SOURCE_LABEL[row.idpSource] ?? DEFAULT_IDP_SOURCE_LABEL,
      renderCell: (params: GridRenderCellParams) => <GridTooltip params={params} />,
    },
    {
      field: USER_ACTIVITY_FIELDS.EVENT,
      headerName: 'Event',
      minWidth: 280,
      flex: 1,
      valueGetter: (_value, row) =>
        row.idpSource === 'fusionauth'
          ? (ACTIVITY_EVENT_TYPE_FUSION_AUTH[
              row.event as keyof typeof ACTIVITY_EVENT_TYPE_FUSION_AUTH
            ] ?? row.description)
          : (ACTIVITY_EVENT_TYPE[row.event as keyof typeof ACTIVITY_EVENT_TYPE] ?? row.description),
      renderCell: (params: GridRenderCellParams) => <GridTooltip params={params} />,
    },
    {
      field: USER_ACTIVITY_FIELDS.CLIENT_NAME,
      headerName: 'Client name',
      flex: 1,
      renderCell: (params: GridRenderCellParams) => <GridTooltip params={params} />,
    },
    {
      field: USER_ACTIVITY_FIELDS.IP_ADDRESS,
      headerName: 'IP address',
      flex: 1,
      renderCell: (params: GridRenderCellParams) => <GridTooltip params={params} />,
    },
    {
      field: USER_ACTIVITY_FIELDS.USER_AGENT,
      headerName: 'User agent',
      flex: 1,
      renderCell: (params: GridRenderCellParams) => <GridTooltip params={params} />,
    },
    {
      field: USER_ACTIVITY_FIELDS.PERFORMED_BY,
      headerName: 'Performed by',
      flex: 1,
      // Writers store the identity's own id when the user acts themselves; show the email instead.
      valueGetter: (_value, row) =>
        row.performedBy === identity.id ? identity.email : row.performedBy,
      renderCell: (params: GridRenderCellParams) => <GridTooltip params={params} />,
    },
  ];

  if (hasErrors) {
    return <ErrorOverlayDataGrid />;
  }

  return (
    <DataGrid
      autoHeight
      columns={columns}
      getRowId={getRowIndex}
      disableEval
      loading={isLoading}
      paginationModel={{ page: pageNumber, pageSize }}
      onPaginationModelChange={({ page, pageSize }) => {
        setPageNumber(page);
        setPageSize(pageSize);
      }}
      initialState={{
        columns: {
          columnVisibilityModel: {
            [USER_ACTIVITY_FIELDS.IP_ADDRESS]: false,
            [USER_ACTIVITY_FIELDS.USER_AGENT]: false,
          },
        },
      }}
      paginationMode="server"
      rowCount={rowCountState}
      pageSizeOptions={[10, 25, 50, 100]}
      rows={rows}
      sx={{
        backgroundColor: 'white',
      }}
    />
  );
};

export default UserActivityDataGrid;
